import _ from 'lodash-es';
import APIV5 from '~/models/api';
import { DEFINED_BY } from '~/scripts/constants/common-kids';
import { ConfirmDeleteModal } from '~/utils/modals';
import Card from '~/scripts/services/model/card';
import PdfUtils from '~/components/pdf/pdf-utils';
import Utils from '~/scripts/services/utils';
import Defaults from '~/scripts/constants/defaults';
import FileUpload from '~/scripts/services/model/file-upload';
import { CreateTypeDoc as CreateTypeDocument } from './modals/create-type-doc/create-type-doc';
import templateModalFilePreview from './modals/modal-file-preview.html';
import ModalFilePreviewController from './modals/modal-file-preview.controller';

/* globals Knitiv */

export default class UploadClassicBlockController {
	/** @type {Knitiv.RootScope} */
	$rootScope;

	/* @ngInject */
	/**
	 * @param {import('angular').IQService} $q
	 * @param {any} NodeService
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').ISCEService} $sce
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 */
	constructor(
		$q,
		NodeService,
		$injector,
		$rootScope,
		$sce,
		$timeout,
		$translate,
		$uibModal,
	) {
		this.$q = $q;
		this.NodeService = NodeService;
		this.$injector = $injector;
		this.$rootScope = $rootScope;
		this.$sce = $sce;
		this.$timeout = $timeout;
		this.$uibModal = $uibModal;

		/** @type {number | undefined} */
		this.index = undefined;

		/** @type {FileUpload | undefined} */
		this.file = undefined;

		/** @type {FileUpload[]} */
		this.files = [];

		/** @type {import('angular').IFormController | undefined} */
		this.form = undefined;

		/** @type {boolean} */
		this.hasCreatePermissions = this.$rootScope.hasCreatePermissions;

		/** @type {import('angular').INgModelController | undefined} */
		this.ngModelCtrl = undefined;

		/** @type {any} */
		this.options = undefined;
	}

	/**
	 * @param {string} query
	 * @param {Event} event
	 * @param {FileUpload} file
	 * @param {number} index
	 */
	async createType(query, event, file, index) {
		const modal = new CreateTypeDocument(this.$injector);
		const result = await modal.open(query);

		/** @type {import('@knitiv/api-client-javascript').ObjectInfoOutput | {kid: string, name: string}} */
		const document = {
			...result,
			kid:  result.objid,
			name: result.objname,
		};

		await this.selectDocumentType(document, event, file, index);
	}

	preview() {
		this.$uibModal.open({
			ariaDescribedBy: 'modal-file-preview-body',
			template:        templateModalFilePreview,
			controller:      ModalFilePreviewController,
			controllerAs:    '$ctrl',
			resolve:         {
				file: () => this.file?.file,
				// @ts-ignore
				url:  () => this.$sce.trustAsResourceUrl(URL.createObjectURL(this.file?.file)),
			},
			size:            'full',
			windowClass:     'modal-dark',
		});
	}

	/**
	 * @param {number} index
	 */
	async remove(index) {
		const answer = !this.file?.isa || await new ConfirmDeleteModal(this.$injector).open(this.file?.name || '');

		if (answer) {
			this.files.splice(index, 1);
		}
	}

	/**
	 * @param {any} document
	 * @param {boolean | Event | null} event
	 * @param {FileUpload | undefined} file
	 * @param {number | undefined} index
	 */
	async selectDocumentType(document, event, file, index) {
		if (!file) {
			return;
		}
		file.ctxs = [];
		file.minLinks = 0;
		file.isa = document?.kid;

		if (file.isa) {
			file.node = await this.NodeService.loadDef(file.isa, null, { components: true });

			// recherche du label date
			const dateComponent = file.node?.components.find(
				c => c.kdata.path.at('-1').kid === 'K_NODE;DATE' && c.kdata.path.at('-1').linker_kid === '0',
			);
			if (dateComponent) {
				file.datePickerOptions.placeholder = dateComponent.label;
				file.showDate = true;
			}

			// ajout des contextes
			if (file.node?.link) {
				for (/** @type {import('~/scripts/services/model/link').default} */ const link of file.node.link) {
					if (link.flow === 'from' && link.relation_kid === DEFINED_BY && link.objnum2_kid) {
						if (![
							'K_NODE;DATE:0',
							'K_NODE;ANNEEREF:0',
							'K_NODE;WF_STATE:0',
							'K_NODE;WORKSPACE:0',
						].includes(`${link.objnum2_kid}:${link.linker_kid}`)) {
							let component = file.node.components.find(c => c.kid === link.kid);

							if (component?.type === 'mf' && component?.options) {
								component.options.displayExpandButton = false;
							}

							// temp fix (if component doesn't exist)
							if (
								component === undefined
								&& link.type_kid !== 'K_NODE;DATE'
							) {
								component = {
									options: {
										displayExpandButton: true,
									},
								};
							}

							/** @type {import("~/scripts/services/model/file-upload").FileContext} */
							const context = {
								card:        new Card(link.card_from, link.card_to),
								checked:     false,
								can_create:  (link.kright & Defaults.DROIT.CREATE) > 0,
								component,
								linker_kid:  link.linker_kid,
								linker_info: {
									kid: link.linker_kid,
									name: link.linker_info.name ?? '',
								},
								links:       [link],
								node:        link.objnum2_kid,
								nodelist:    [link.objnum2_kid],
								placeholder: link.objname ?? '',
								type:        link.type_kid ?? 'K_NODE;STRING',
								visible:     false,
							};
							file.ctxs.push(context);
							file.minLinks += link.card_from;

							this.$timeout(() => {
								context.visible = this.options.hideContextsFromLinks
									? !file.links.some(l => l.isa === link.objnum2_kid) : true;
							});
						}
						else if (link.objnum2_kid === 'K_NODE;WF_STATE') {
							file.haveWorkflow = true;
							file.ctx_workflow = {
								node:       link.objnum2_kid,
								label:      link.objname,
								card:       new Card(link.card_from, link.card_to),
								checked:    false,
								profillist: {},
								statelist:  {},
							};
						}
					}
				}

				// @ts-ignore
				if (file.file.type === 'application/pdf') {
					/** @type {any} */
					const fields = await APIV5.instance.docExtractFieldsGet({
						class_kid:     file.isa,
						// @ts-ignore
						file_mimetype: file.file.type,
						// @ts-ignore
						file_name:     file.file.name,
						// @ts-ignore
						file_size:     file.file.size,
					}, { id: 'upload-classic-block.select-document-type' });

					if (Array.isArray(fields?.value?.elts) && fields?.value?.elts.length > 0) {
						const options = {
							search: fields.value.elts,
						};
						const pdfUtils = new PdfUtils(this.$injector);
						// @ts-ignore
						const rawPDF = await file.file.arrayBuffer();
						const propositions = await pdfUtils.extractText(rawPDF, options);
						if (propositions?.length > 0) {
							file.fillFormSubmission(propositions);
						}
					}
				}
			}

			// le type de document a changé
			if (file.oldIsaKid !== file.isa) {
				if (file.node?.wf.eligible && file.node.wf.list.length > 0) {
					// clean selected workflows
					file.workflow.map(/** @type {any} */ w => {
						if (w.kid && !file.node?.wf.list.includes(w.kid)) {
							w.allocator.length = 0;
							w.allocators.length = 0;
							w.commentaire = '';
							w.kid = undefined;
							w.state = null;
							w.states.length = 0;
						}
						return w;
					});
				}
				else {
					file.workflow.length = 0;
				}

				if (file.oldIsaKid && file.links.length > 0) {
					// on supprime les liens incompatibles avec les nouveaux ctxs
					_.remove(file.links, link => !file.ctxs.some(context => context.node === link.isa));
				}
				file.oldIsaKid = file.isa;
			}

			// propagation des infos
			if (event) {
				if (index === 0) {
					for (const [entryIndex, entryFile] of this.files.entries()) {
						if (entryFile.sync && entryIndex !== 0) {
							entryFile.loading = true;
							entryFile.setIsa(document);
							entryFile.showDate = file.showDate;
							await this.selectDocumentType(document, null, entryFile, entryIndex);
							entryFile.loading = false;
						}
					}
				}
				else if (document.kid !== this.files[0].isa && index !== undefined) {
					this.files[index].sync = false;
				}
			}

			this.$timeout(() => {
				file.wf = {
					...file.node?.wf,
					showAddButton: true,
				};
				this.$timeout(() => {
					this.$rootScope.$apply();
					this.$timeout(() => this.$rootScope.$apply(), 500);
				});
			});

			await this.updateNamePrefix(file);
		}
		else {
			file.showDate = false;
		}
	}

	/**
	 * @param {FileUpload} file
	 * @param {number} index
	 */
	async updateDate(file, index) {
		if (index === 0) {
			for (const [entryIndex, entryFile] of this.files.entries()) {
				if (entryFile.sync && entryIndex !== 0) {
					entryFile.date = file.date;
				}
			}
		}
		else if (!file.loading) {
			this.files[index].sync = false;
		}

		if (file?.isa) {
			await this.updateNamePrefix(file);
		}
	}

	/**
	 * @param {FileUpload} file
	 */
	async updateNamePrefix(file) {
		// appel kParamSearch
		if (file.isa) {
			const data = await APIV5.instance.namePrefixExists(
				{ class_kid: file.isa },
				{ id: 'upload-classic-block.update-name-prefix' },
			);
			file.prefixIsNeeded = false;
			file.prefix = null;
			if (data) {
				file.prefixIsNeeded = data.result === true;
				if (file.prefixIsNeeded && file.isa) {
					const itemInfo = {
						links: {
							CONTEXT: file.links.map(link => ({
								class_kid:  link.class_kid,
								isa:        link.isa,
								kid:        link.kid,
								linker_kid: link.linker_kid,
								name:       link.name,
							})),
							TEMPS:   typeof file.date === 'string' ? file.date : `${Utils.isoDate(file.date)}Z`,
						},
					};

					// noinspection JSCheckFunctionSignatures
					const namePrefix = await APIV5.instance.namePrefixGet(
						{
							isa:       file.isa,
							suffixe:   file.name || '',
							item_info: itemInfo,
						},
						{ id: 'upload-classic-block.update-name-prefix' },
					);
					file.prefix = namePrefix?.prefix || null;
				}
			}
		}

		this.$timeout(() => {
			this.$rootScope.$apply();
		});
	}

	$onInit() {
		if (this.file?.isa) {
			(async () => this.selectDocumentType({ kid: this.file?.isa }, true, this.file, this.index))();
			this.$timeout(() => {
				this.$rootScope.$apply();
			});
		}
	}
}
